import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack, Switch } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider, Controller, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IPrismSettingsDTO, getPrismSettings, savePrismSettings } from 'wisestep-api';
import { ControlledTextInput, Label, WsButton } from 'wisestep-base';
import { object, string } from 'yup';

import { SettingsHeader } from '../../components/SettingsHeader';
import SettingsIndex from '../../components/SettingsIndex';
import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../hooks/useAtsCustomAPIAccess';
import { success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

import JobBoardLogins from './JobBoardLogins';

const PrismSettings = () => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspaceContext = useWorkspaceContext();
  const [prismSettingsData, setPrismSettingsData] = useState<IPrismSettingsDTO>();
  const formDefaultValues = {
    proxy: prismSettingsData?.proxy ?? '',
    userName: prismSettingsData?.userName ?? '',
    password: prismSettingsData?.password ?? '',
    enabled: prismSettingsData?.enabled ?? false,
  };
  const validationSchema = object().shape({
    proxy: string().required('IP Address with is required'),
    userName: string().required('Username is required'),
    password: string().required('Password is required'),
  });
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: formDefaultValues,
  };

  const methods = useForm(formOptions);
  const { handleSubmit, control, reset, setValue } = methods;

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_PRISM_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  useEffect(() => {
    (async function () {
      const getPrismSettingsData = await getPrismSettings();
      if (getPrismSettingsData.data) {
        const temp = getPrismSettingsData.data;
        setPrismSettingsData(getPrismSettingsData.data);
        reset(temp);
      }
    })();
  }, [reset]);
  const proxySettingsHandler = async (data: FieldValues) => {
    try {
      const payload: IPrismSettingsDTO = {
        enabled: data.enabled,
        password: data.password,
        proxy: data.proxy,
        userName: data.userName,
        workspaceId: data.workspaceId,
      };
      await savePrismSettings(payload);
      setNotification(success('Prism Extension Settings,Saved Successfully.'));
    } catch (error) {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };

  return (
    <Box>
      <WsBreadCrumbs
        breadCrumbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['PRISM_SETTINGS']
        )}
      />
      <SettingsHeader title="Prism Settings" />
      <Grid container spacing={2}>
        <Grid item xs={3} mt={2} borderRadius={1.5}>
          <SettingsIndex
            options={[
              {
                label: 'Prism Extension Settings',
                url: '/prism-extension-settings/',
              },
              {
                label: 'User Management',
                url: '/prism-user-management/',
              },
              {
                label: 'Session Management',
                url: '/prism-session-management/',
              },
            ]}
          />
        </Grid>
        <Grid item xs={9} mt={2} borderRadius={1.5}>
          <FormProvider {...methods}>
            <ControlledTextInput
              name="proxy"
              labelText="Proxy IP (Separated by port number):*"
              type="text"
              fullWidth
              id="ipAddress"
              placeholder="100.000.000:2019"
              variant="outlined"
              required
            />
            <ControlledTextInput
              name="userName"
              labelText="Proxy Username:"
              type="text"
              fullWidth
              id="userName"
              placeholder="Username"
              variant="outlined"
              required
            />
            <ControlledTextInput
              name="password"
              labelText="Proxy proxyPassword:"
              type="text"
              fullWidth
              id="password"
              placeholder="Password"
              variant="outlined"
              required
            />
            <Controller
              name="enabled"
              control={control}
              render={(props) => (
                <Grid container direction="column">
                  <Label>Proxy Enable:</Label>
                  <Switch
                    checked={props.field.value}
                    onChange={(_event, checked) => setValue('enabled', checked)}
                  />
                </Grid>
              )}
            />

            <Stack mt={2} spacing={2} direction="row">
              <WsButton
                onClick={() => navigate('/app-settings/apps-and-integrations')}
                variant="outlined"
              >
                Cancel
              </WsButton>
              {manageAccess && (
                <WsButton
                  onClick={handleSubmit(proxySettingsHandler)}
                  type="submit"
                  variant="contained"
                >
                  Save Changes
                </WsButton>
              )}
            </Stack>
          </FormProvider>
          <Grid mt={2} container direction="column">
            <Label>View or Add Logins by Selecting Job Board</Label>
            <JobBoardLogins />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
export default PrismSettings;
